import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Input } from '../../ui';
import { updatePortalSettings } from '../../../services/api';
import { useUserStore } from '../../../store/userStore';
import { ColorSelect } from './ColorSelect';
import LogoUploader from './LogoUploader';
import { useUIStore } from '../../../store/uiStore';
import { PremiumBrandingAlert } from './PremiumBrandingAlert';

interface PortalSettingsProps {
  settings?: {
    title?: string;
    themeColor?: string;
    logoUrl?: string;
    defaultLogoUrl?: string;
    companyUrl?: string;
  };
  isProCloudPlan: boolean;
  context?: 'guide' | 'portal'; // Context determines label wording
}

function isValidHttpUrl(str: string) {
  if(!str) {
    return true;
  }

  let url;
  
  try {
    url = new URL(str);
  } catch (error) {
    console.log(error);
    return false;  
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

const PortalSettings: React.FC<PortalSettingsProps> = ({ settings, isProCloudPlan, context = 'portal' }) => {
  const { t } = useTranslation();
  const isGuideContext = context === 'guide';
  const { updateProperties } = useUserStore();
  const { setIsSaving } = useUIStore();
  const [localSettings, setLocalSettings] = useState({
    title: settings?.title || '',
    themeColor: settings?.themeColor || '',
    companyUrl: settings?.companyUrl || '',
    logoUrl: settings?.logoUrl || '',
    defaultLogoUrl: settings?.defaultLogoUrl || '',
  });

  const [validation, setValidation] = useState({
    title: '',
    companyUrl: ''
  });

  const updateSettings = async (updates: Partial<typeof localSettings>, logoFile?: File | null, removeFile?: boolean) => {
    const newSettings = { ...localSettings, ...updates };
    setLocalSettings(newSettings);
    
    // Validate settings (title is optional; only companyUrl format is validated)
    const newValidation = {
      title: '',
      companyUrl: (newSettings.companyUrl && !isValidHttpUrl(newSettings.companyUrl)) ? t('portal.settings.companyUrlInvalid') : ''
    };
    setValidation(newValidation);
    // Don't update if there are validation errors
    const hasErrors = Object.values(newValidation).some(error => error !== '');
    if (hasErrors) {
      return;
    }
    try {
      setIsSaving(true);
      const response = await updatePortalSettings({
        settings: {
          title: newSettings.title,
          themeColor: newSettings.themeColor,
          logoUrl: newSettings.logoUrl,
          companyUrl: newSettings.companyUrl,
        } as any,
        logoFile: logoFile || undefined,
        removeFile: removeFile || undefined,
      });
      
      if (response.ok && response.properties?.portal) {
        updateProperties({ portal: response.properties.portal });
      }
      
    } catch (error) {
      console.error('Error updating portal settings:', error);
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  };

  const handleTitleChange = (title: string) => {
    updateSettings({ title });
  };

  const handleThemeColorChange = (themeColor: string) => {
    updateSettings({ themeColor });
  };

  const handleCompanyUrlChange = (companyUrl: string) => {
    updateSettings({ companyUrl });
  };

  const handleRemoveLogo = () => {
    updateSettings({ logoUrl: '' }, null, true);
  };

  const handleLogoUpload = (file: File) => {
    // Create a preview URL for immediate display
    const url = URL.createObjectURL(file);
    updateSettings({ logoUrl: url }, file, false);
  };

  return (
    <div className="container mx-0">
      {!isProCloudPlan && (
        <div className="mb-6">
          <PremiumBrandingAlert context={isGuideContext ? 'guide' : 'help-center'} />
        </div>
      )}
      <div
        className={`bg-white border rounded-md p-8 mb-6 ${
          !isProCloudPlan ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        {isGuideContext && (
          <div className="mb-6 pb-4 border-b">
            <p className="text-sm text-muted-foreground">
              {t('portal.settings.guideIntro')}
            </p>
          </div>
        )}
      <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-6">
          <div className="space-y-2 flex flex-row items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {isGuideContext ? t('portal.settings.companyNameLabel') : t('portal.settings.portalNameLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {isGuideContext
                  ? t('portal.settings.nameDescGuide')
                  : t('portal.settings.nameDescPortal')}
              </p>
              {validation.title && (
                <p className="text-sm text-red-500">{validation.title}</p>
              )}
            </div>
            <Input
              type="text"
              value={localSettings.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              maxLength={60}
              className="h-8 text-gray-500 text-sm border rounded px-2 !w-[250px] ml-auto"
            />
          </div>

          <div className="space-y-2 flex  flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('portal.settings.companyUrlLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground text-wrap mr-2">
                {t('portal.settings.companyUrlDescription')}
              </p>
              {validation.companyUrl && (
                <p className="text-sm text-red-500">{validation.companyUrl}</p>
              )}
            </div>
            <Input
              type="text"
              value={localSettings.companyUrl}
              onChange={(e) => handleCompanyUrlChange(e.target.value)}
              maxLength={60}
              className="h-8 text-gray-500 text-sm border rounded px-2 !w-[250px] ml-auto"
            />
          </div>

          <div className="space-y-2 flex flex-wrap  flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {isGuideContext ? t('portal.settings.colorSchemeGuideLabel') : t('portal.settings.colorSchemePortalLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {isGuideContext
                  ? t('portal.settings.colorSchemeGuideDesc')
                  : t('portal.settings.colorSchemePortalDesc')}
              </p>
            </div>
            <ColorSelect
              value={localSettings.themeColor}
              onChange={handleThemeColorChange}
            />
          </div>

          <div className="space-y-2 flex flex-wrap  flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {isGuideContext ? t('portal.settings.companyLogoLabel') : t('portal.settings.portalLogoLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {isGuideContext
                  ? t('portal.settings.logoDescGuide')
                  : t('portal.settings.logoDescPortal')}
              </p>
            </div>
            <div className="w-[250px]">
              <LogoUploader
                value={localSettings.logoUrl}
                onChange={(url) => updateSettings({ logoUrl: url })}
                onRemove={handleRemoveLogo}
                onUploadFile={handleLogoUpload}
                defaultLogo={localSettings.defaultLogoUrl}
                maxSize={2}
                maxHeight={1000}
                maxWidth={1000}
                acceptedTypes={["image/jpeg", "image/png"]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default PortalSettings;